define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/_base/sniff",
	"dojo/aspect",
	"dojo/dom-class",
	"dojo/keys",
	"dijit/focus",
	"dijit/form/DropDownButton",
	"dijit/popup",
	"dijit/Tooltip",
	"dijit/TooltipDialog",
	"csi/_base/underscore",
	"csi/constants",
	"csi/html",
	"csi/locale",
	"csi/middleware/DelayFilteringService",
	"csi/reflection",
	"csi/state",
	"csi/state/rootStore",
	"csi/utility/coreUtility",
	"csi/widget/Automation",
	"csi/widget/CheckedMultiSelectDropdown",
	"csi/widget/DateRange",

	"./Preferences"     // ensure that constants are populated
], function(declare, lang, has, aspect, domClass, keys, focusUtil, DropDownButton, popup, Tooltip, TooltipDialog,
	_, constants, html, locale, {enableDelayFiltering, enableRecents, getDelayFilteringKeysForAttribute, getRecentValues, processRecentSelections}, 
	reflection, state, { routerStore, sessionStore, }, {objectsAreEqual}, Automation, CheckedMultiSelectDropdown, DateRange) {

	var _DropDownButton = declare(DropDownButton, {
		/**
		 * @override
		 * Disable the behavior that resize to match the width of the column for filtering 
		 */
		autoWidth: false,

		/**
		 * @override
		 * Modeless Action is ON when inline controller is active (inline edit), If so, do not open AdHoc, let inline editor finish first.
		 */
		openDropDown: function() {
			if (state.get("modelessAction")) {
				this.focus();
				return;
			}

			return this.inherited(arguments);
		}
	});

	return declare(null, {
		// DOMNode: table row containing filters
		_filterRowNode: null,

		// Object: map of spec id to button
		_filterButtons: null,

		// Object: map of spec id to button
		_tooltips: null,

		// timeout handle, for internal use only
		_disableOnClickTimeoutHandle: null,

		/** @property {boolean} originalSuspend - original value of suspend of this view  */
		originalSuspend: null,

		postCreate: function() {
			// summary: register events with table view
			var view = this.getView();

			this.inherited(arguments);
			this.own(aspect.after(view, "onBuildHead", lang.hitch(this, this._onBuildHead), true));
			this.own(aspect.after(view, "onViewPreferences", () => this._onViewPreferences(), true));
		},

		uninitialize: function() {
			// summary: cleanup pointers
			this._cleanupFilters();
			this.inherited(arguments);
		},

		validatePlugin: function(/*Context*/ctx) {
			// summary: do not render plugin if filtering not allowed or when printing
			return ctx.view.canFilter() && !ctx.bPrint;
		},

		_cleanupFilters: function() {
			// summary: remove column filter widgets
			var tr = this._filterRowNode;
			if (tr) {
				html.clear(tr, true);
				html.orphan(tr);
				delete this._filterRowNode;
				delete this._filterButtons;
				delete this._tooltips;
			}
		},

		_loadDropDown: async function(/*DropDownButton*/button, /*String*/id, /*Function*/callback) {
			// summary: initialize TooltipDialog drop-down when button is first pressed
			const view = this.getView();
			const spec = view.getSpec(id);
			const widgetClass = spec.isDateAttribute() ? DateRange : CheckedMultiSelectDropdown;
			const { userUrl } = sessionStore;
			const { locationNodes } = routerStore;
			const { urlRoot, urlSubtype} = view;
			const subtypeName = urlSubtype && reflection.getSubtypeName(urlSubtype);
			const cache = view.getCache();
			const rootNode = cache.get(urlRoot);

			const bDelayFiltering = enableDelayFiltering(spec);
			const bRecents = enableRecents(spec);
			let bShowHybridList = true;
			let recentValues = new Set();

			if (bDelayFiltering) {
				bShowHybridList = !this.originalSuspend;
			}

			if (bRecents) {
				try {
					const filteringKeys = await getDelayFilteringKeysForAttribute(rootNode, userUrl, spec, locationNodes, subtypeName);
					recentValues = await getRecentValues(filteringKeys);
				} catch (ex) {
					// don't block the application, just log them as error
					console.error(ex);
				}
			}

			var widgetParams = {
				bAdHoc: true,
				view,
				bDelayFiltering,
				bRecents,
				bShowHybridList, 
				bOpenOnFocus: true,
				"class": "csi-table-filter",
				recentValues,

				onBlur: function() {
					if (w._opened) {
						button.closeDropDown();
					}
				},
				onChange: async (value) => {
					if (value.length != 0 && bRecents) {
						try {
							const filteringKeysForWriting = await getDelayFilteringKeysForAttribute(rootNode, userUrl, spec, locationNodes, subtypeName, false);
							const latestValues = await processRecentSelections(value, filteringKeysForWriting);

							// update recentValues on widget
							w.recentValues = latestValues;
						} catch (ex) {
							// don't block the application, just log them as error
							console.error(ex);
						}
					}

					this._onChangeFilter(id, value);
					if (w._opened) {
						button.closeDropDown();
					}
				},
				onEscape: function() {
					button.closeDropDown();
				}
			};
			var w = new widgetClass(widgetParams);

			// Keeps watch on filter when it is opened or closed
			w.watch("_opened", (name, oldValue, newValue) => {
				if (!newValue) {
					// setTimeout necessary because if user makes selection in dropdown
					// and then clicks in table to close dropdown row can be selected.
					this._disableOnClickTimeoutHandle = setTimeout(() => {
						view.set("disableOnClick", newValue);
						this._disableOnClickTimeoutHandle = null;
					}, 300);
				} else {
					// If this._disableOnClickTimeoutHandle timeout is pending - clear this , preventing setting disableOnClick = false in timeout handler
					if (this._disableOnClickTimeoutHandle) {
						clearTimeout(this._disableOnClickTimeoutHandle);
						this._disableOnClickTimeoutHandle = null;
					}
					view.set("disableOnClick", newValue);
				}
			});

			button.dropDown = new TooltipDialog({
				"class": "csiInlineDialog",
				"content": w,
				onBlur: function() {
					w._bChangeOnBlur = false;
				},
				onCancel: function() {
					w._bChangeOnBlur = false;
				},
				onFocus: () => {
					// reinitialize widget parameters when opening tooltip dialog
					if (!w._bChangeOnBlur) {
						w._bChangeOnBlur = true;

						// recentValues could change in onChange above, sync up here
						widgetParams.recentValues = w.recentValues;
						this._updateValueNode(lang.mixin(view.getSpecValuesWidgetParams(id), widgetParams), w);
					}

					w.focus();
				}
			});

			aspect.after(w, "onInput", function(evt) {
				var key = evt.charCode || evt.keyCode;
				if (key == keys.TAB && button.dropDown && button.dropDown.focused) {
					popup.close(button.dropDown);
				}
				// C8-45897 Firefox Only: when close adhoc filter, it not being destroyed but rather hide.
				// Blur event will not trigger under firefox (IMHO FF bug) when you close adhoc with escape or tab.
				// Blur does happen if you click outside to close dropdown, then no bug and works fine.
				// If blur not happen, hidden input still has focus (FF only), and if you click on it again
				// it will open dropdown and dropdown receive focus, then blur event happen on editor and dropdown close (bug).
				//  Not happening with other browsers as blur event fire there when tooltip dialog and input become hidden.
				if (has("mozilla") && (key == keys.ESCAPE || key == keys.TAB)) {
					focusUtil.curNode && focusUtil.curNode.blur();
				}
			}, true);
			button.dropDown.startup();

			callback();

			this.connect(html.pageContentScroll(), "onscroll", function(event) {
				if (button.dropDown && button.dropDown.focused) {
					popup.close(button.dropDown);
				}
			});
		},

		_onBuildHead: function(/*Context*/ctx, /*DOMNode[]*/rows) {
			// summary: build filter row for each column if that is desired position
			var view = this.getView();

			if (!view.get("disableAdHoc") && !ctx.bExcel) {
				const tr = view._generateTableRow(["csi-table-filters", html.CSS_NO_EXPORT]);

				this._cleanupFilters();
				this._filterButtons = {};
				this._tooltips = {};

				// create filter row
				var specs = view.getQueryLevelSpecs();
				var key = view.defView.getHtmlKey();

				specs.traverse(true, function(c, sc) {
					var th = view.generateHeaderCell(ctx, c, sc, tr);
					var spec = sc || c;
					var id = spec.queryKey;
					if (spec.canFilter()) {
						// create filter control - setup to lazy create TooltipDialog first time the button is clicked
						var button = this._filterButtons[id] = new _DropDownButton(Automation.params(key, "filter", id));

						this._tooltips[id] = new Tooltip({
							connectId: [button.domNode],
							position: ["below-centered", "above-centered"]
						});

						// set this after constructor so that it does not cause problem with CSS classes added to widget -
						// this is old style of setting the position (but I think the only way to do what we want)
						button.set("dropDownPosition", [
							{aroundCorner: "TL", corner: "TL"},
							{aroundCorner: "TR", corner: "TR"}
						]);

						this._updateButton(id);
						button.placeAt(th);
						button.loadDropDown = lang.hitch(this, this._loadDropDown, button, id);
					}
				}, this);

				// store filter row if filtering is supported for at least one column
				if (!_.isEmpty(this._filterButtons)) {
					this._filterRowNode = tr;
					rows.push(tr);
				}
			}
		},

		_onChangeFilter: function(/*String*/id, /*Any[]*/value) {
			// summary: process change to column filter value(s)
			this._bInternalChange = true;
			this._changeFilter(id, value || []);
			this._updateButton(id);
			this._bInternalChange = false;
		},

		_onWatchQuery: function(/*String*/attr, /*Object*/oldVal, /*Object*/newVal) {
			// summary: update buttons when query changes
			this.inherited(arguments);

			if (this._filterButtons && !this._bInternalChange) {
				// update buttons for changed/removed filters
				_.each(oldVal, function(value, id) {
					if (!_.isEqual(value, newVal[id])) {
						this._updateButton(id);
					}
				}, this);

				// update buttons for added filters
				_.each(newVal, function(value, id) {
					if (!oldVal[id]) {
						this._updateButton(id);
					}
				}, this);
			}
		},

		_onViewPreferences() {
			const view = this.getView();
			this.originalSuspend = view.get("suspended") && !view.suspendedUntilScrolledIntoView;
		},

		_updateButton: function(/*String*/id) {
			// summary: update label to display filter value
			var button = this._filterButtons[id];
			if (button && !button._destroyed) {
				var view = this.getView();
				var value = view.getQueryValueAsLabel(id);
				var n = value ? value.length : 0;
				var text;

				if (!n) {
					text = locale.resource("SelectAll");
				} else if (n > 1) {
					text = locale.resource("SelectMultiple");
				} else {
					text = value[0];
				}

				button.set("label", _.escape(text));

				// show selected value in italic if substring match rather than equal
				domClass.toggle(button.domNode, "italic", (n == 1) && view.hasSingleQueryValue(id, true));

				// update tooltip
				this._updateTooltipNode(this._tooltips[id], id);
			}
		}
	});
});

